/**
 * Pulls a signed-in fan with no profile, or with `onboardingStep: "username"`
 * or `"details"`, out of any app route and into `/onboarding`. Mounted ONCE
 * around `<Outlet />` in `__root.tsx`, so every present and future route is
 * covered with no per-route wiring.
 *
 * A component, not a `useEffect` inside each screen, for the same reason
 * `apps/app/src/auth/requireSignedIn.ts` gates in `beforeLoad`: an
 * effect-based redirect renders the protected content first, a visible flash
 * of somebody's pools before the router bounces them to onboarding.
 */
import { useProfileGet } from "@sportsmet/profile-sdk/private";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { type ReactNode, useEffect } from "react";

import { useAuth } from "../../auth/AuthProvider";
import { RoutePendingScreen } from "../../shell/RoutePendingScreen";
import { pendingInviteRedirect } from "./onboardingRedirect";
import { ONBOARDING_PATH, resolveOnboardingSlug } from "./onboardingSteps";

export const OnboardingGate = ({
  children,
}: {
  children: ReactNode;
}): ReactNode => {
  const { state } = useAuth();
  const navigate = useNavigate();
  const { pathname, href } = useRouterState({
    select: (routerState) => ({
      pathname: routerState.location.pathname,
      href: routerState.location.href,
    }),
  });
  const isSignedIn = state.status === "signedIn";
  const isOnOnboarding = pathname.startsWith(ONBOARDING_PATH);

  const query = useProfileGet({}, { enabled: isSignedIn && !isOnOnboarding });

  const isFailed = query.isError || Boolean(query.data?.error);
  const slug =
    query.data === undefined || isFailed
      ? null
      : resolveOnboardingSlug({ profile: query.data.data?.profile ?? null });

  useEffect(() => {
    // A disabled TanStack Query v5 observer still reports whatever is
    // already in the shared cache, so `slug` can resolve even while the fan
    // is already on `/onboarding`. The render-path short circuit below does
    // not stop this effect from firing, so it has to check `isOnOnboarding`
    // itself.
    if (isOnOnboarding || isFailed || slug === null) return;
    const redirect = pendingInviteRedirect(href);
    void navigate({
      to: ONBOARDING_PATH,
      search: { step: slug, ...(redirect === undefined ? {} : { redirect }) },
      replace: true,
    });
  }, [slug, isFailed, isOnOnboarding, href]);

  // Order matters: the disabled short-circuit is checked before the pending
  // branch below, because TanStack Query v5 reports `isPending: true` on a
  // query with `enabled: false`. Getting this backwards renders the pending
  // screen forever for every signed-out visitor.
  if (!isSignedIn || isOnOnboarding) return children;

  if (query.isPending) {
    return (
      <div data-testid="onboarding-gate-pending">
        <RoutePendingScreen />
      </div>
    );
  }

  // A failed profile read lets the fan through. Being wrong in this direction shows them an app screen; being wrong the other way locks them out of the product on one bad response.
  if (isFailed) return children;

  if (slug === null) return children;

  return (
    <div data-testid="onboarding-gate-pending">
      <RoutePendingScreen />
    </div>
  );
};
